// On-page replacements for window.confirm / window.alert.
// Browser pop-ups are silently blocked in some embedded browsers (e.g. in-app browser panes), which made
// "spend 1 token?" auto-cancel so Host Quiz did nothing. These render inside the page instead.
//
//   if (!(await appConfirm("Start a live quiz?", { confirmLabel: "Spend 1 token" }))) return;
//   appAlert("Saved!");   // non-blocking; await it to wait until the host presses OK
import React, { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";

let root = null;
const queue = [];
let showing = false;

function ensureRoot() {
  if (root) return root;
  const el = document.createElement("div");
  el.id = "app-dialog-root";
  document.body.appendChild(el);
  root = createRoot(el);
  return root;
}

function showNext() {
  if (showing || queue.length === 0) return;
  showing = true;
  const item = queue.shift();
  const close = (value) => {
    ensureRoot().render(null);
    showing = false;
    item.resolve(value);
    showNext();
  };
  ensureRoot().render(<DialogBox {...item.options} message={item.message} kind={item.kind} onClose={close} />);
}

function open(kind, message, options = {}) {
  // Automated tests: every message is logged, and a test may answer instead of a person
  // (window.__appDialogAutoAnswer is only ever set by the E2E suite).
  try {
    (window.__appDialogLog = window.__appDialogLog || []).push({ kind, message: String(message ?? "") });
    if (typeof window.__appDialogAutoAnswer === "function") {
      const answer = window.__appDialogAutoAnswer(kind, String(message ?? ""));
      if (answer !== undefined) return Promise.resolve(kind === "confirm" ? !!answer : undefined);
    }
  } catch {}
  return new Promise((resolve) => {
    queue.push({ kind, message: String(message ?? ""), options, resolve });
    showNext();
  });
}

/** Resolves true (confirm) or false (cancel / Esc / click outside). */
export function appConfirm(message, options = {}) {
  return open("confirm", message, options);
}

/** Resolves when dismissed. */
export function appAlert(message, options = {}) {
  return open("alert", message, options);
}

function DialogBox({ kind, message, title, confirmLabel, cancelLabel, danger, onClose }) {
  const okRef = useRef(null);
  const isConfirm = kind === "confirm";

  useEffect(() => {
    okRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(isConfirm ? false : undefined); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isConfirm, onClose]);

  // First paragraph becomes the heading when no title is given.
  const paragraphs = message.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const heading = title || (paragraphs.length > 1 ? paragraphs.shift() : null);

  const btn = {
    height: 40,
    padding: "0 18px",
    borderRadius: 11,
    fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
    fontWeight: 800,
    fontSize: "0.95rem",
    cursor: "pointer",
  };

  return (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(isConfirm ? false : undefined); }}
      style={{
        position: "fixed", inset: 0, zIndex: 2147483000,
        background: "rgba(10, 8, 30, 0.6)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        role={isConfirm ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-label={heading || "Message"}
        style={{
          width: "min(460px, 100%)", maxHeight: "85vh", overflowY: "auto",
          background: "linear-gradient(180deg, #2b2262 0%, #1c1744 100%)",
          border: "1px solid rgba(255, 224, 102, 0.35)", borderRadius: 18,
          boxShadow: "0 18px 60px rgba(0,0,0,0.55)", color: "#f6f4ff",
          fontFamily: "'Montserrat','Open Sans',Arial,sans-serif", padding: "22px 22px 18px",
        }}
      >
        {heading && (
          <div style={{ fontSize: "1.15rem", fontWeight: 900, color: "#ffe066", marginBottom: 10, lineHeight: 1.3 }}>
            {heading}
          </div>
        )}
        {paragraphs.map((p, i) => (
          <p key={i} style={{ margin: "0 0 10px", lineHeight: 1.45, whiteSpace: "pre-line", fontSize: "0.97rem" }}>{p}</p>
        ))}
        <form
          onSubmit={(e) => { e.preventDefault(); onClose(isConfirm ? true : undefined); }}
          style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}
        >
          {isConfirm && (
            <button
              type="button"
              onClick={() => onClose(false)}
              style={{ ...btn, background: "rgba(255,255,255,0.1)", color: "#f6f4ff", border: "1px solid rgba(255,255,255,0.3)" }}
            >
              {cancelLabel || "Cancel"}
            </button>
          )}
          <button
            ref={okRef}
            type="submit"
            style={{
              ...btn,
              border: "1px solid rgba(255,255,255,0.3)",
              color: danger ? "#fff" : "#08321b",
              background: danger
                ? "linear-gradient(180deg, #ff7070 0%, #d63333 100%)"
                : "linear-gradient(180deg, #43e08a 0%, #1d9e57 100%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45), 0 3px 10px rgba(0,0,0,0.35)",
            }}
          >
            {confirmLabel || "OK"}
          </button>
        </form>
      </div>
    </div>
  );
}
